import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Avatar,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import { UserProfile } from '../../models/Profile';
import { AdminIndividualTutorUser } from '../../models/AdminIndividualTutorUser';
import { FakeUserInfo, CreateFakeUserRequest } from '../../models/FakeUser';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/AuthService';
import AddIcon from '@mui/icons-material/Add';
import { formatMobileAppLabel } from '../../utils/mobileAppLabel';

type PaginatedUserTab = 'real' | 'hosts' | 'tutors' | 'individualTutors';
type UserTab = PaginatedUserTab | 'fake';

const TAB_INDEX: Record<UserTab, number> = {
  real: 0,
  hosts: 1,
  tutors: 2,
  individualTutors: 3,
  fake: 4,
};

const INDEX_TO_TAB: UserTab[] = ['real', 'hosts', 'tutors', 'individualTutors', 'fake'];

const isPaginatedUserTab = (tab: UserTab): tab is PaginatedUserTab => tab !== 'fake';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `user-tab-${index}`,
    'aria-controls': `user-tabpanel-${index}`,
  };
}

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<UserTab>('real');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [hosts, setHosts] = useState<AdminIndividualTutorUser[]>([]);
  const [tutors, setTutors] = useState<AdminIndividualTutorUser[]>([]);
  const [individualTutors, setIndividualTutors] = useState<AdminIndividualTutorUser[]>([]);
  const [fakeUsers, setFakeUsers] = useState<FakeUserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assumedUser] = useState(authService.getAssumedUser());
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalElements, setTotalElements] = useState(0);
  const [fakeUserDialogOpen, setFakeUserDialogOpen] = useState(false);
  const [fakeUserForm, setFakeUserForm] = useState<CreateFakeUserRequest>({
    name: '',
    bandName: '',
  });
  const [fakeUserLoading, setFakeUserLoading] = useState(false);

  useEffect(() => {
    if (!isPaginatedUserTab(activeTab)) {
      return;
    }

    const fetchPaginatedUsers = async () => {
      try {
        setLoading(true);
        const { page, pageSize } = paginationModel;
        const data = await ({
          real: () => adminApiClient.getUsers(page, pageSize),
          hosts: () => adminApiClient.getHostUsers(page, pageSize),
          tutors: () => adminApiClient.getTutorUsers(page, pageSize),
          individualTutors: () => adminApiClient.getIndividualTutorUsers(page, pageSize),
        }[activeTab])();

        const rows = Array.isArray(data.content) ? data.content : [];
        const rowsWithIds = rows.map((user, index) => ({
          ...user,
          id: user.id || `${activeTab}-${index}`,
        }));

        ({
          real: setUsers,
          hosts: setHosts,
          tutors: setTutors,
          individualTutors: setIndividualTutors,
        }[activeTab])(rowsWithIds as any);

        setTotalElements(data.page?.totalElements ?? 0);
      } catch (err) {
        const errorMessages: Record<PaginatedUserTab, string> = {
          real: t('users.fetchError', 'Failed to fetch users'),
          hosts: t('users.fetchHostsError', 'Failed to fetch hosts'),
          tutors: t('users.fetchTutorsError', 'Failed to fetch tutors'),
          individualTutors: t('users.fetchIndividualTutorsError', 'Failed to fetch individual tutors'),
        };
        setError(errorMessages[activeTab]);
        console.error(`Error fetching ${activeTab} users:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaginatedUsers();
  }, [t, paginationModel, activeTab]);

  useEffect(() => {
    const fetchFakeUsers = async () => {
      try {
        setLoading(true);
        const data = await adminApiClient.listFakeUsers();
        setFakeUsers(data);
      } catch (err) {
        setError(t('users.fetchFakeUsersError', 'Failed to fetch fake users'));
        console.error('Error fetching fake users:', err);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'fake') {
      fetchFakeUsers();
    }
  }, [t, activeTab]);

  const handleSwitchTo = (userId: string, userName?: string) => {
    const user =
      users.find(u => u.id === userId) ??
      hosts.find(u => u.id === userId) ??
      tutors.find(u => u.id === userId) ??
      individualTutors.find(u => u.id === userId);
    const name = user?.name ?? userName;
    if (name && userId) {
      authService.setAssumedUser(userId, name);
      navigate('/user/profile');
      window.location.reload();
    }
  };

  const handleSwitchBack = () => {
    authService.clearAssumedUser();
    window.location.reload();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(INDEX_TO_TAB[newValue] ?? 'real');
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const handleOpenFakeUserDialog = () => {
    setFakeUserForm({ name: '', bandName: '' });
    setFakeUserDialogOpen(true);
  };

  const handleCloseFakeUserDialog = () => {
    setFakeUserDialogOpen(false);
    setFakeUserForm({ name: '', bandName: '' });
  };

  const handleCreateFakeUser = async () => {
    if (!fakeUserForm.name || !fakeUserForm.bandName) {
      setError(t('users.fakeUserValidationError', 'Please fill in all fields'));
      return;
    }

    try {
      setFakeUserLoading(true);
      await adminApiClient.createFakeUser(fakeUserForm);
      handleCloseFakeUserDialog();
      // Refresh fake users list
      const data = await adminApiClient.listFakeUsers();
      setFakeUsers(data);
    } catch (err) {
      setError(t('users.createFakeUserError', 'Failed to create fake user'));
      console.error('Error creating fake user:', err);
    } finally {
      setFakeUserLoading(false);
    }
  };

  const handleDeleteFakeUser = async (userId: string) => {
    try {
      await adminApiClient.deleteFakeUser(userId);
      // Refresh fake users list
      const data = await adminApiClient.listFakeUsers();
      setFakeUsers(data);
    } catch (err) {
      setError(t('users.deleteFakeUserError', 'Failed to delete fake user'));
      console.error('Error deleting fake user:', err);
    }
  };

  const fakeUserColumns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: t('users.name', 'Name'), 
      flex: 1.5,
    },
    { 
      field: 'bandName', 
      headerName: t('users.bandName', 'Band Name'), 
      flex: 1.5,
    },
    { 
      field: 'createdAt', 
      headerName: t('users.createdAt', 'Created At'), 
      flex: 1,
      renderCell: (params: any) => {
        const date = params.row?.createdAt;
        if (!date) return '';
        try {
          return format(new Date(date), 'MMM d, yyyy HH:mm');
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params: any) => {
        const userId = params.row.id;
        if (!userId) return null;
        return (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDeleteFakeUser(userId)}
          >
            {t('users.delete', 'Delete')}
          </Button>
        );
      },
    },
  ];

  const columns: GridColDef[] = [
    { 
      field: 'profileImageUrl', 
      headerName: '', 
      width: 60,
      sortable: false,
      renderCell: (params: any) => (
        <Avatar 
          src={params.row.profileImageUrl} 
          alt={params.row.name}
          sx={{ width: 32, height: 32 }}
        >
          {params.row.name?.[0]?.toUpperCase() || '?'}
        </Avatar>
      )
    },
    { 
      field: 'name', 
      headerName: t('users.name', 'Name'), 
      flex: 1.5,
    },
    { 
      field: 'bandName', 
      headerName: t('users.bandName', 'Band Name'), 
      flex: 1.5,
      renderCell: (params: any) => (
        <Typography variant="body2" color={params.row.bandName ? 'text.primary' : 'text.secondary'}>
          {params.row.bandName || '—'}
        </Typography>
      )
    },
    { 
      field: 'phoneNumber', 
      headerName: t('users.phoneNumber', 'Phone Number'), 
      flex: 1,
    },
    {
      field: 'platforms',
      headerName: t('users.app', 'App'),
      width: 100,
      sortable: false,
      renderCell: (params: any) => {
        const label = formatMobileAppLabel(params.row?.platforms, params.row?.mobileAppInstalled);
        const lastSeen = params.row?.lastAppSeenAt;
        let lastSeenLabel = '';
        if (lastSeen) {
          try {
            lastSeenLabel = format(new Date(lastSeen), 'MMM d, yyyy HH:mm');
          } catch {
            lastSeenLabel = '';
          }
        }
        const cell = (
          <Typography
            variant="body2"
            color={label === '—' ? 'text.secondary' : 'text.primary'}
            sx={{ fontSize: '0.875rem' }}
          >
            {label}
          </Typography>
        );
        if (!lastSeenLabel) {
          return cell;
        }
        return (
          <Tooltip title={t('users.lastAppSeenAt', 'Last seen {{date}}', { date: lastSeenLabel })}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
              {cell}
            </Box>
          </Tooltip>
        );
      },
    },
    { 
      field: 'createdAt', 
      headerName: t('users.registeredAt', 'Registered At'), 
      flex: 1,
      renderCell: (params: any) => {
        const date = params.row?.createdAt;
        if (!date) return '';
        try {
          return format(new Date(date), 'MMM d, yyyy HH:mm');
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params: any) => {
        const userId = params.row.id;
        if (!userId) return null;
        const isAssumedUser = userId === assumedUser.id;
        return (
          <Button
            variant={isAssumedUser ? "contained" : "outlined"}
            color={isAssumedUser ? "secondary" : "primary"}
            size="small"
            onClick={() => isAssumedUser ? handleSwitchBack() : handleSwitchTo(userId)}
          >
            {isAssumedUser ? t('users.switchBack', 'Switch Back') : t('users.switchTo', 'Switch To')}
          </Button>
        );
      },
    },
  ];

  const organizationNameColumn: GridColDef = {
    field: 'organizationName',
    headerName: t('users.organizationName', 'Organization'),
    flex: 1.5,
    renderCell: (params: any) => (
      <Typography variant="body2" color={params.row.organizationName ? 'text.primary' : 'text.secondary'}>
        {params.row.organizationName || '—'}
      </Typography>
    ),
  };

  const individualTutorColumns: GridColDef[] = [
    ...columns.slice(0, 2),
    ...columns.slice(3),
  ];

  const organizationMemberColumns: GridColDef[] = [
    ...columns.slice(0, 2),
    organizationNameColumn,
    ...columns.slice(3),
  ];

  const paginatedUsersByTab: Record<PaginatedUserTab, Array<UserProfile | AdminIndividualTutorUser>> = {
    real: users,
    hosts,
    tutors,
    individualTutors,
  };

  const paginatedColumnsByTab: Record<PaginatedUserTab, GridColDef[]> = {
    real: columns,
    hosts: organizationMemberColumns,
    tutors: organizationMemberColumns,
    individualTutors: individualTutorColumns,
  };

  const emptyUsersMessageByTab: Record<PaginatedUserTab, string> = {
    real: t('users.noUsers', 'No users found'),
    hosts: t('users.noHosts', 'No hosts found'),
    tutors: t('users.noTutors', 'No tutors found'),
    individualTutors: t('users.noIndividualTutors', 'No individual tutors found'),
  };

  const paginatedUsers = isPaginatedUserTab(activeTab) ? paginatedUsersByTab[activeTab] : [];
  const paginatedColumns = isPaginatedUserTab(activeTab) ? paginatedColumnsByTab[activeTab] : columns;
  const emptyUsersMessage = isPaginatedUserTab(activeTab) ? emptyUsersMessageByTab[activeTab] : '';
  const showOrganizationName = activeTab === 'hosts' || activeTab === 'tutors';
  const showBandName = activeTab === 'real';

  if (loading && users.length === 0 && hosts.length === 0 && tutors.length === 0 && individualTutors.length === 0 && fakeUsers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1400, width: '100%', mx: 'auto' }}>
      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" component="h1">
            {t('users.title', 'Users')}
          </Typography>
          {activeTab === 'fake' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenFakeUserDialog}
            >
              {t('users.addFakeUser', 'Add Fake User')}
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={TAB_INDEX[activeTab]}
            onChange={handleTabChange}
            aria-label="user tabs"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab
              label={t('users.plain', 'Plain')}
              {...a11yProps(0)}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minHeight: 56,
                px: 3,
              }}
            />
            <Tab
              label={t('users.hosts', 'Hosts')}
              {...a11yProps(1)}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minHeight: 56,
                px: 3,
              }}
            />
            <Tab
              label={t('users.tutors', 'Tutors')}
              {...a11yProps(2)}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minHeight: 56,
                px: 3,
              }}
            />
            <Tab
              label={t('users.individualTutors', 'Individual Tutors')}
              {...a11yProps(3)}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minHeight: 56,
                px: 3,
              }}
            />
            <Tab
              label={t('users.fake', 'Fake')}
              {...a11yProps(4)}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minHeight: 56,
                px: 3,
              }}
            />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isPaginatedUserTab(activeTab) && (
        <Box sx={{ pt: 3 }}>
          {/* Mobile List View */}
          {isMobile ? (
          <>
            {paginatedUsers.length === 0 ? (
              <Box sx={{ 
                mt: 4, 
                mb: 4,
                textAlign: 'center',
                py: 6,
                bgcolor: 'background.default',
                borderRadius: 2
              }}>
                <Typography variant="body1" color="text.secondary">
                  {emptyUsersMessage}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2, mx: -1 }}>
                {paginatedUsers.filter(user => user.id).map((user) => {
                  const userId = user.id!;
                  const isAssumedUser = userId === assumedUser.id;
                  const orgUser = user as AdminIndividualTutorUser;
                  return (
                    <Box
                      key={userId}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: isAssumedUser ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: isAssumedUser ? 'rgba(79, 95, 235, 0.08)' : 'background.paper',
                        transition: 'all 0.2s ease',
                        boxShadow: isAssumedUser ? '0 2px 8px rgba(79, 95, 235, 0.15)' : 'none',
                      }}
                    >
                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                      <Avatar 
                        src={user.profileImageUrl} 
                        alt={user.name}
                        sx={{ width: 48, height: 48 }}
                      >
                        {user.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            fontSize: '1rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.name}
                        </Typography>
                        {showOrganizationName && orgUser.organizationName && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {orgUser.organizationName}
                          </Typography>
                        )}
                        {showBandName && user.bandName && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {user.bandName}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                          {user.phoneNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {t('users.app', 'App')}:{' '}
                          {formatMobileAppLabel(user.platforms, user.mobileAppInstalled)}
                          {user.lastAppSeenAt
                            ? ` · ${format(new Date(user.lastAppSeenAt), 'MMM d, yyyy')}`
                            : ''}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Divider sx={{ mb: 1 }} />
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        mb: 1,
                      }}
                    >
                      {t('users.registeredAt', 'Registered at')}: {user.createdAt ? format(new Date(user.createdAt), 'MMM d, yyyy HH:mm') : ''}
                    </Typography>
                    
                    <Button
                      variant={isAssumedUser ? "contained" : "outlined"}
                      color={isAssumedUser ? "secondary" : "primary"}
                      size="small"
                      onClick={() => isAssumedUser ? handleSwitchBack() : handleSwitchTo(userId, user.name)}
                      fullWidth
                    >
                      {isAssumedUser ? t('users.switchBack', 'Switch Back') : t('users.switchTo', 'Switch To')}
                    </Button>
                  </Box>
                  );
                })}
                
                {/* Simple pagination controls for mobile */}
                {paginatedUsers.length > 0 && totalElements > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('users.showing', 'Showing')} {Math.min(paginationModel.page * paginationModel.pageSize + 1, totalElements)} - {Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalElements)} {t('users.of', 'of')} {totalElements}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <button
                        onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 })}
                        disabled={paginationModel.page === 0}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '4px',
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          backgroundColor: paginationModel.page === 0 ? theme.palette.action.disabledBackground : theme.palette.background.paper,
                          color: paginationModel.page === 0 ? theme.palette.text.disabled : theme.palette.text.primary,
                          cursor: paginationModel.page === 0 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t('users.previous', 'Previous')}
                      </button>
                      <button
                        onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 })}
                        disabled={(paginationModel.page + 1) * paginationModel.pageSize >= totalElements}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '4px',
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          backgroundColor: (paginationModel.page + 1) * paginationModel.pageSize >= totalElements ? theme.palette.action.disabledBackground : theme.palette.background.paper,
                          color: (paginationModel.page + 1) * paginationModel.pageSize >= totalElements ? theme.palette.text.disabled : theme.palette.text.primary,
                          cursor: (paginationModel.page + 1) * paginationModel.pageSize >= totalElements ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t('users.next', 'Next')}
                      </button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </>
          ) : (
            /* Desktop Table View */
            <DataGrid
              rows={paginatedUsers}
              columns={paginatedColumns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={totalElements}
              paginationMode="server"
              pageSizeOptions={[10, 25, 50]}
              loading={loading}
              autoHeight
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeader': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          )}
        </Box>
        )}

        {/* Fake Users Tab */}
        <TabPanel value={TAB_INDEX[activeTab]} index={4}>
          {isMobile ? (
            <>
              {fakeUsers.length === 0 ? (
                <Box sx={{ 
                  mt: 4, 
                  mb: 4,
                  textAlign: 'center',
                  py: 6,
                  bgcolor: 'background.default',
                  borderRadius: 2
                }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('users.noFakeUsers', 'No fake users found')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2, mx: -1 }}>
                  {fakeUsers.map((user) => (
                    <Box
                      key={user.id}
                      sx={{
                        mb: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                        {user.name}
                      </Typography>
                      {user.bandName && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                          {user.bandName}
                        </Typography>
                      )}
                      {user.createdAt && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                          {t('users.createdAt', 'Created at')}: {format(new Date(user.createdAt), 'MMM d, yyyy HH:mm')}
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteFakeUser(user.id)}
                        fullWidth
                      >
                        {t('users.delete', 'Delete')}
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <DataGrid
              rows={fakeUsers}
              columns={fakeUserColumns}
              loading={loading}
              autoHeight
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeader': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          )}
        </TabPanel>

      {/* Create Fake User Dialog */}
      <Dialog
        open={fakeUserDialogOpen}
        onClose={handleCloseFakeUserDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {t('users.createFakeUser', 'Create Fake User')}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label={t('users.name', 'Name')}
              value={fakeUserForm.name}
              onChange={(e) => setFakeUserForm({ ...fakeUserForm, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('users.bandName', 'Band Name')}
              value={fakeUserForm.bandName}
              onChange={(e) => setFakeUserForm({ ...fakeUserForm, bandName: e.target.value })}
              required
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseFakeUserDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFakeUser}
            disabled={fakeUserLoading || !fakeUserForm.name || !fakeUserForm.bandName}
          >
            {fakeUserLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('users.create', 'Create')
            )}
          </Button>
        </DialogActions>
      </Dialog>
      </Card>
    </Box>
  );
};

export default UsersPage;

